// Package 4, part A.4 (owner, 7. 10. 2026; the world book's "Prva sedmica"): a new account's First Week. 9. 10. 2026
// (owner): one list of 21 goals in the order a new player can do them, without days on the card; a goal whose system the
// schedule has not opened yet is grey with "Opens with …" (the system opens on its day or level, schedule.ts) and counts
// only once it is open. Each goal pays a little (10–30 Scrap or one consumable) when it is done; 7, 14 and 21 goals done
// pay the book's rewards (the seven day rewards before, the same in total), 21 the title "Made It a Week" and a Rare item
// on the survivor's level. The card stays on Camp above the season path until all 21 are done. Only accounts made after
// the package 4 deploy have it. 10. 10. 2026 (owner, deploy7): no days anymore: the card is "First Steps", the title
// "Still Standing", and a goal still closed says only the level it opens at ("Opens at level 4").

import type { FeatureId } from './unlocks';
import type { StackMap } from './types';
import { scheduleOpensAt, scheduleRow } from './schedule';

export type FirstWeekGoalId =
  | 'send'
  | 'report'
  | 'equip'
  | 'attributes'
  | 'same'
  | 'daily'
  | 'seasonGoal'
  | 'craft'
  | 'meal'
  | 'storage'
  | 'tobias'
  | 'horde'
  | 'skill'
  | 'stance'
  | 'encounter'
  | 'repairEarly'
  | 'dismantle'
  | 'long'
  | 'book'
  | 'radio'
  | 'night';

export interface FirstWeekGoal {
  id: FirstWeekGoalId;
  /** The text in the game (the book's). */
  text: string;
  /** What it pays when done. */
  reward: StackMap;
  /** 9. 10. 2026: the system that opens it (grey before: "Opens at level N", its level on the schedule). */
  opens?: FeatureId;
  /** 10. 10. 2026: the level it waits for without a system of its own (long runs, the night run). */
  level?: number;
  /** A part of the game to come. */
  waits?: { part?: 'radio' };
}

/** 9. 10. 2026 (owner): what a milestone pays (an item: its rarity on the survivor's level). */
export interface FirstWeekReward {
  stacks?: StackMap;
  item?: 'uncommon' | 'rare';
  title?: string;
}

/**
 * 9. 10. 2026 (owner): 7, 14 and 21 goals done pay these, the seven day rewards before put together (the same total: 3
 * Bandages, 4 Rations, 30 Scrap, 40 Wood, 30 Metal, a Medkit, 20 Electronics, an Uncommon and a Rare item, the title).
 */
export const FIRST_WEEK_MILESTONES: { goals: number; reward: FirstWeekReward }[] = [
  { goals: 7, reward: { stacks: { bandage: 3, ration: 2, scrap: 30 } } },
  { goals: 14, reward: { stacks: { wood: 40, metal: 30, medkit: 1 }, item: 'uncommon' } },
  { goals: 21, reward: { stacks: { electronics: 20, ration: 2 }, item: 'rare', title: 'Still Standing' } },
];

/** The day rewards accounts were paid before 9. 10. 2026 (`daysPaid`): a milestone pays only what they did not get yet. */
const DAY_REWARDS_BEFORE: Record<number, FirstWeekReward> = {
  1: { stacks: { bandage: 3 } },
  2: { stacks: { ration: 2, scrap: 30 } },
  3: { stacks: { wood: 40, metal: 30 } },
  4: { stacks: { medkit: 1 } },
  5: { item: 'uncommon' },
  6: { stacks: { electronics: 20, ration: 2 } },
  7: { item: 'rare', title: 'Still Standing' },
};
const DAYS_OF_MILESTONE: Record<number, number[]> = { 7: [1, 2], 14: [3, 4, 5], 21: [6, 7] };

/** What a milestone still pays: its reward less the day rewards of its days paid before (old accounts). */
export function milestoneReward(goals: number, daysPaid: readonly number[] = []): FirstWeekReward {
  const m = FIRST_WEEK_MILESTONES.find((x) => x.goals === goals)!.reward;
  const stacks: StackMap = { ...(m.stacks ?? {}) };
  let item = m.item;
  let title = m.title;
  for (const d of DAYS_OF_MILESTONE[goals] ?? []) {
    if (!daysPaid.includes(d)) continue;
    const r = DAY_REWARDS_BEFORE[d];
    for (const [k, v] of Object.entries(r.stacks ?? {}) as [keyof StackMap, number][]) stacks[k] = Math.max(0, (stacks[k] ?? 0) - v);
    if (r.item === item) item = undefined;
    if (r.title === title) title = undefined;
  }
  for (const k of Object.keys(stacks) as (keyof StackMap)[]) if (!stacks[k]) delete stacks[k];
  return { ...(Object.keys(stacks).length ? { stacks } : {}), ...(item ? { item } : {}), ...(title ? { title } : {}) };
}

/** 9. 10. 2026 (owner): the list, in the order a new player can do the goals (the first session first). */
export const FIRST_WEEK_GOALS: FirstWeekGoal[] = [
  { id: 'send', text: 'Send your survivor out.', reward: { scrap: 10 } },
  { id: 'report', text: 'Read your first report.', reward: { scrap: 10 } },
  { id: 'equip', text: 'Put on something you found.', reward: { bandage: 1 } },
  { id: 'attributes', text: 'Spend your attribute points.', reward: { bandage: 1 } },
  { id: 'same', text: 'Send a run with Same orders.', reward: { painkillers: 1 } },
  { id: 'daily', text: 'Finish a contract.', reward: { scrap: 15 } },
  { id: 'seasonGoal', text: 'Claim a season path goal.', reward: { scrap: 30 }, opens: 'season' },
  { id: 'craft', text: 'Make something at the Workshop.', reward: { scrap: 15 }, opens: 'craft' },
  { id: 'meal', text: 'Eat before you head out.', reward: { ration: 1 }, opens: 'meals' },
  { id: 'storage', text: 'Upgrade Storage.', reward: { scrap: 20 }, opens: 'buildings' },
  { id: 'tobias', text: 'Do business with Tobias.', reward: { scrap: 20 }, opens: 'trader' },
  { id: 'horde', text: "Fight in tonight's horde (or stand guard).", reward: { scrap: 20 }, opens: 'hordeJoin' },
  { id: 'skill', text: 'Learn a skill.', reward: { scrap: 30 }, opens: 'skills' },
  { id: 'stance', text: "Change your survivor's stance.", reward: { scrap: 20 } },
  { id: 'encounter', text: 'Get through an encounter with another survivor.', reward: { scrap: 25 } },
  { id: 'repairEarly', text: 'Repair something before it breaks.', reward: { repair_kit: 1 } },
  { id: 'dismantle', text: "Dismantle what you don't need.", reward: { scrap: 25 } },
  { id: 'long', text: 'Send a run of 2 hours or more.', reward: { scrap: 25 }, level: scheduleRow('longRuns').level },
  // 13. 10. 2026 (owner): the book is open from the start, so the goal is too.
  { id: 'book', text: 'Open the Collection book.', reward: { scrap: 25 } },
  { id: 'radio', text: 'Listen to Danny Dawn.', reward: { scrap: 25 }, opens: 'radio', waits: { part: 'radio' } },
  { id: 'night', text: 'Bring back a night run.', reward: { stim: 1 }, level: scheduleRow('nightRun').level },
];

/** 10. 10. 2026: the level a goal opens at (its system's level on the schedule, or its own). */
export function firstWeekGoalLevel(g: FirstWeekGoal): number {
  return g.opens ? (scheduleOpensAt(g.opens)?.level ?? 1) : (g.level ?? 1);
}

/** The parts of the game a goal can wait for (the radio came with part L, 8. 10. 2026). */
export const FIRST_WEEK_PARTS: Record<'radio', boolean> = { radio: true };

/** A night run (owner, 7. 10. 2026): 2 hours or more, sent between 20:00 and 04:00 in the player's time. */
export const NIGHT_RUN = { minMinutes: 120, fromHour: 20, toHour: 4 };

export function isNightSend(baseMinutes: number, localHour: number): boolean {
  return baseMinutes >= NIGHT_RUN.minMinutes && (localHour >= NIGHT_RUN.fromHour || localHour < NIGHT_RUN.toHour);
}

export interface FirstWeekState {
  /** When each goal was done. */
  done: Partial<Record<FirstWeekGoalId, number>>;
  /** 9. 10. 2026: the milestones paid (7, 14, 21). */
  milestonesPaid?: number[];
  /** Before 9. 10. 2026: the days whose reward was paid (a milestone pays only the rest). */
  daysPaid: number[];
  /** Before 9. 10. 2026: runs claimed since day 3 opened ("Come back from 3 runs"; gone). */
  runsFromDay3?: number;
  /** All 21 done (the title and the Rare item paid). */
  finished?: number;
}

export const newFirstWeek = (): FirstWeekState => ({ done: {}, milestonesPaid: [], daysPaid: [] });

export const firstWeekGoal = (id: FirstWeekGoalId) => FIRST_WEEK_GOALS.find((g) => g.id === id)!;

/**
 * Why a goal waits (for the card), or null: it can be done. 9. 10. 2026: by its system (`open`: the survivor's systems
 * now), the level it waits for, or a part of the game to come; 10. 10. 2026 (owner): it says only "Opens at level N".
 */
export function firstWeekWait(g: FirstWeekGoal, open: (f: FeatureId) => boolean, level: number): string | null {
  if (g.waits?.part && !FIRST_WEEK_PARTS[g.waits.part]) return 'Radio comes soon';
  if ((g.opens && !open(g.opens)) || level < (g.level ?? 0)) return `Opens at level ${firstWeekGoalLevel(g)}`;
  return null;
}

/** The milestones reached and not paid yet. */
export function firstWeekMilestonesDue(s: FirstWeekState): number[] {
  const n = FIRST_WEEK_GOALS.filter((g) => s.done[g.id] != null).length;
  return FIRST_WEEK_MILESTONES.filter((m) => n >= m.goals && !(s.milestonesPaid ?? []).includes(m.goals)).map((m) => m.goals);
}

/**
 * The card: how many of 21 are done, the three milestones, and the goals in order: the ones that can be done now (in the
 * list's order), then the ones still closed (grey, "Opens at level N"), then the ones done.
 */
export interface FirstWeekDTO {
  doneCount: number;
  total: number;
  milestones: { goals: number; reward: FirstWeekReward; paid: boolean }[];
  goals: { id: FirstWeekGoalId; text: string; reward: StackMap; done: boolean; lock: string | null }[];
}

export function firstWeekView(s: FirstWeekState, open: (f: FeatureId) => boolean, level: number): FirstWeekDTO {
  const rows = FIRST_WEEK_GOALS.map((g) => {
    const done = s.done[g.id] != null;
    return { id: g.id, text: g.text, reward: g.reward, done, lock: done ? null : firstWeekWait(g, open, level) };
  });
  return {
    doneCount: rows.filter((r) => r.done).length,
    total: FIRST_WEEK_GOALS.length,
    milestones: FIRST_WEEK_MILESTONES.map((m) => ({ goals: m.goals, reward: m.reward, paid: (s.milestonesPaid ?? []).includes(m.goals) })),
    goals: [...rows.filter((r) => !r.done && !r.lock), ...rows.filter((r) => !r.done && r.lock), ...rows.filter((r) => r.done)],
  };
}
